/** @jsx jsx */
import { useState, useRef } from 'react'
import { ModalBase, Input, Loader, Badge } from '@livechat/design-system'
import { css, jsx, keyframes } from '@emotion/core'

import noAnswersIcon from '../assets/icons/no-asnwers.svg'
import MarkdownRenderer from './MarkdownRenderer'

const showModalAnimation = keyframes`
from {
 transform: translateY(20px);
}
`

const chartCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 400px;
  background-color: white;

  width: calc(100% - 40px);
  max-width: 500px;
  padding: 20px;
  padding-top: 25px;
  border-radius: 10px;
  box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

  :nth-of-type(2n) {
    margin-right: auto;
  }

  :nth-of-type(2n + 1) {
    margin-left: auto;
  }

  @media (max-width: 980px) {
    margin: 0 !important;
  }

  .question {
    font-weight: 400;
    text-align: center;
    margin-bottom: 30px;
    font-size: 18px;
  }

  .answers {
    display: flex;
    flex-direction: column;
    padding: 0 50px;
    max-width: 500px;
  }

  .answer {
    font-weight: 100;
    display: flex;
    margin-bottom: 15px;
  }

  .lc-badge {
    background-color: rgb(63, 140, 255);
    margin-right: 10px;
  }

  .text {
    line-height: 20px;
    width: calc(100% - 20px);
    overflow: hidden;
    word-break: break-word;
    max-height: 100px;
  }

  .no-answer {
    color: hsl(0, 0%, 60%);
    line-height: 20px;
    width: calc(100% - 20px);
  }

  .lc-btn {
    width: 200px;
    margin: 0 auto;
    margin-top: 10px;
  }

  .modal-answers {
    display: flex;
    flex-direction: column;
    max-height: 740px;
    padding: 50px;
    padding-top: 0;
    overflow-y: scroll;
    margin-bottom: 50px;

    .lc-input {
      margin: 0 auto;
      margin-bottom: 20px;
      width: 350px;
    }
  }

  .modal-answer {
    display: flex;
    padding-bottom: 20px;
    margin-bottom: 20px;

    :not(:last-child) {
      border-bottom: 1px solid hsl(0, 0%, 90%);
    }
  }

  .no-results {
    margin: 0 auto;
  }

  .loader {
  }
`

const moreCss = css`
  color: #3f8cff;
  font-size: 17px;
  margin-top: 2px;
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`

const TextAnswerChart = ({
  field,
  fieldIndex,
  response,
  isLoading,
  surveyId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [query, setQuery] = useState('')
  const input = useRef(null)

  const currentResponse = response.map((el) => el.response[fieldIndex])
  const formattedResponse = currentResponse.slice(0, 5)

  const filteredResponse = query
    ? currentResponse.filter((el) =>
        el.toLowerCase().includes(query.toLowerCase())
      )
    : currentResponse

  return (
    <div css={chartCss} key={`${surveyId}-text-answer-chart-${fieldIndex}`}>
      <div className="question">
        <MarkdownRenderer>{field.question}</MarkdownRenderer>
      </div>
      {isLoading ? (
        <Loader className="loader" size="large" />
      ) : response && response.length > 0 ? (
        <div className="answers">
          {formattedResponse.map((answer, answerIndex) => {
            return (
              <div
                className="answer"
                key={`${surveyId}-text-answer-chart-${fieldIndex}-${answerIndex}`}
              >
                <Badge>{answerIndex + 1}</Badge>
                {answer ? (
                  <div className="text">{answer}</div>
                ) : (
                  <div className="no-answer">no answer</div>
                )}
              </div>
            )
          })}
          {response.length > 5 && (
            <span onClick={() => setIsModalOpen(true)} css={moreCss}>
              Show more responses
            </span>
          )}
          {isModalOpen && (
            <ModalBase
              onClose={() => setIsModalOpen(false)}
              css={css`
                z-index: 100;
                width: 800px;
                height: 840px;
                animation: ${showModalAnimation} 0.2s ease;

                .lc-input {
                  margin: 50px calc(50% - 150px);
                  margin-bottom: 30px;
                }
              `}
            >
              <Input
                ref={input}
                value={query}
                placeholder="Search for an answer"
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="modal-answers">
                {filteredResponse.length > 0 ? (
                  filteredResponse.map((answer, answerIndex) => {
                    return (
                      <div
                        className="modal-answer"
                        key={`${surveyId}-text-answer-chart-answers-${fieldIndex}-${answerIndex}`}
                      >
                        <Badge>{answerIndex + 1}</Badge>
                        {answer ? (
                          <div className="text">{answer}</div>
                        ) : (
                          <div className="no-answer">no answer</div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <div className="no-results">No results</div>
                )}
              </div>
            </ModalBase>
          )}
        </div>
      ) : (
        <span
          css={css`
            display: flex;
            align-items: center;
            color: hsl(0, 0%, 70%);
            font-weight: 100;

            > img {
              margin-right: 5px;
            }
          `}
        >
          <img src={noAnswersIcon} alt="no answers" />
          <span>No answers given</span>
        </span>
      )}
    </div>
  )
}

export default TextAnswerChart
